import { useEffect, useRef, useState } from 'react';

/**
 * Typewriter animation hook.
 *
 * Given a fixed `prefix` and a `suffix` that changes, animates the
 * transition: erases the old suffix char by char, then types the new
 * one char by char. Returns the current visible text and whether the
 * blinking cursor should show.
 *
 * Example:
 *   prefix = "What "
 *   suffix toggles between "did you work on?" and "do you want to work on?"
 *   → "What did you work on?" erases to "What " then types "What do you want to work on?"
 */
export function useTypewriter(
  prefix: string,
  suffix: string,
  speed = 40,
): { text: string; showCursor: boolean } {
  const [displayedSuffix, setDisplayedSuffix] = useState(suffix);
  const [showCursor, setShowCursor] = useState(false);
  const targetRef = useRef(suffix);
  const animatingRef = useRef(false);

  useEffect(() => {
    if (suffix === targetRef.current && !animatingRef.current) return;
    targetRef.current = suffix;

    if (animatingRef.current) return; // let the running animation pick up the new target
    animatingRef.current = true;
    setShowCursor(true);

    let cancelled = false;

    const animate = async () => {
      // Erase current suffix char by char
      let current = displayedSuffix;
      while (current.length > 0 && !cancelled) {
        current = current.slice(0, -1);
        await new Promise<void>((r) => {
          if (cancelled) return r();
          setTimeout(() => {
            if (!cancelled) setDisplayedSuffix(current);
            r();
          }, speed);
        });
      }

      if (cancelled) return;

      // Type new suffix char by char
      const target = targetRef.current;
      let i = 0;
      while (i < target.length && !cancelled) {
        i++;
        const slice = target.slice(0, i);
        await new Promise<void>((r) => {
          if (cancelled) return r();
          setTimeout(() => {
            if (!cancelled) setDisplayedSuffix(slice);
            r();
          }, speed);
        });
      }

      if (!cancelled) {
        animatingRef.current = false;
        // If target changed during animation, re-run
        if (targetRef.current !== target) {
          animate();
        } else {
          setShowCursor(false);
        }
      }
    };

    animate();

    return () => {
      cancelled = true;
    };
    // We intentionally only react to suffix changes, not displayedSuffix
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suffix, speed]);

  return {
    text: prefix + displayedSuffix,
    showCursor,
  };
}
